import { CredentialType, CredentialEntry } from "../../utils/crypto/vault-crypto";

export const credentialTypes: { value: CredentialType; label: string; icon: string; description: string }[] = [
  { value: "password", label: "Login / Password", icon: "🔑", description: "Username and password for websites or apps" },
  { value: "api_key", label: "API Key", icon: "🔐", description: "API keys, tokens, and secrets" },
  { value: "ssh_key", label: "SSH Key", icon: "🖥️", description: "SSH private keys and passphrases" },
  { value: "certificate", label: "Certificate", icon: "📜", description: "SSL/TLS certificates and keys" },
  { value: "database", label: "Database", icon: "🗄️", description: "Database connection credentials" },
  { value: "server", label: "Server", icon: "🖧", description: "Server access credentials" },
  { value: "secure_note", label: "Secure Note", icon: "📝", description: "Encrypted text notes" },
  { value: "wifi", label: "Wi-Fi", icon: "📶", description: "Wi-Fi network passwords" },
  { value: "credit_card", label: "Credit Card", icon: "💳", description: "Payment card information" },
  { value: "custom", label: "Custom", icon: "⚙️", description: "Custom credential type" }
];

export const entryTypes = [
  { value: "text", label: "Text", icon: "📝" },
  { value: "password", label: "Password", icon: "🔑" },
  { value: "username", label: "Username", icon: "👤" },
  { value: "email", label: "Email", icon: "📧" },
  { value: "url", label: "URL", icon: "🔗" }
] as const;

export const getLabelPlaceholder = (type: CredentialType): string => {
  switch (type) {
    case "password": return "e.g., Admin Portal, User Role Credentials";
    case "api_key": return "e.g., Stripe Prod Key, AWS Access Token";
    case "ssh_key": return "e.g., Prod Web Server, DB Bastion Host";
    case "database": return "e.g., Main PostgreSQL, Auth Redis Cache";
    case "server": return "e.g., Nginx Frontend, Backend Node JS";
    case "certificate": return "e.g., *.example.com SSL, Internal CA";
    case "wifi": return "e.g., Office Guest Network, Warehouse IoT";
    case "credit_card": return "e.g., Corporate Amex, AWS Billing Visa";
    case "secure_note": return "e.g., Office Alarm Code, Safe Combination";
    default: return "e.g., Enter a descriptive label";
  }
};

export const getDefaultEntries = (type: CredentialType): CredentialEntry[] => {
  switch (type) {
    case "password":
      return [
        { key: "Username", value: "", type: "username" },
        { key: "Password", value: "", type: "password" }
      ];
    case "api_key":
      return [
        { key: "API Key", value: "", type: "text" },
        { key: "Secret", value: "", type: "password" }
      ];
    case "ssh_key":
      return [
        { key: "Private Key", value: "", type: "text" },
        { key: "Passphrase", value: "", type: "password" }
      ];
    case "database":
      return [
        { key: "Host", value: "", type: "text" },
        { key: "Port", value: "", type: "text" },
        { key: "DB Name", value: "", type: "text" },
        { key: "Username", value: "", type: "username" },
        { key: "Password", value: "", type: "password" }
      ];
    case "server":
      return [
        { key: "Host/IP", value: "", type: "text" },
        { key: "Port", value: "22", type: "text" },
        { key: "Username", value: "", type: "username" },
        { key: "Password", value: "", type: "password" }
      ];
    case "certificate":
      return [
        { key: "Certificate", value: "", type: "text" },
        { key: "Private Key", value: "", type: "password" },
        { key: "CA Bundle", value: "", type: "text" }
      ];
    case "wifi":
      return [
        { key: "SSID", value: "", type: "text" },
        { key: "Password", value: "", type: "password" },
        { key: "Security", value: "WPA2-PSK", type: "text" }
      ];
    case "credit_card":
      return [
        { key: "Cardholder", value: "", type: "text" },
        { key: "Card Number", value: "", type: "text" },
        { key: "Expiry", value: "", type: "text" },
        { key: "CVV", value: "", type: "password" }
      ];
    case "secure_note":
      return [];
    default:
      return [{ key: "", value: "", type: "text" }];
  }
};
